import { Avatar, Box, Heading, HStack, IconButton, Image, Stack, Tag, Text, Tooltip } from '@chakra-ui/react';
import { getAsset } from '@pelicargo/tenants';
import { Show } from '@pelicargo/ui';
import { Fragment, MouseEvent, useCallback } from 'react';
import { HiPencil, HiPlus, HiXMark } from 'react-icons/hi2';

import { useCurrentRequest } from '../hooks/useCurrentRequest';
import { useRequestDrawersContext } from './RequestDrawers';
import { Association, ContactHit, useSimpleContactSearch } from './SimpleContactSearch/Context';

type ContactCardProps = {
  contact: ContactHit & {
    existsInSystem?: boolean;
  };
  isSelected: boolean;
  onSelect?: (contact: ContactHit) => void;
  onRemove?: (contact: ContactHit) => void;
  onUpdate?: (contact: ContactHit) => void;
};

export const ContactCard = ({ contact, isSelected, onSelect, onUpdate, onRemove }: ContactCardProps) => {
  const { refreshContactSearch, onRemoveContact, onSelectContact } = useSimpleContactSearch();

  const { openModal, closeModal } = useRequestDrawersContext();
  const { request } = useCurrentRequest();

  const borderColor = isSelected ? 'orange.500' : 'border.emphasized';
  const bg = contact?.existsInSystem === false ? 'rgba(221, 107, 32, 0.2)' : undefined;
  const name = contact?.full_name;
  const avatarName = name?.split('')?.[0];

  const handleClick = useCallback(() => {
    // only allow selection if onSelect is defined
    if (!onSelect) return;

    if (isSelected) return onRemove?.(contact);
    return onSelect?.(contact);
  }, [isSelected, onSelect, onRemove, contact]);

  const renderAssociation = useCallback((association: Association, index: number) => {
    const parts = [];

    if (association?.providers?.length) parts.push(association.providers.join(', '));
    if (association?.airports?.length) parts.push(association.airports.join(', '));
    if (association?.tags?.length) parts.push(association.tags.join(', '));
    if (association?.accounts?.length) parts.push(association.accounts?.map((account) => account?.id).join(', '));

    const tooltipContent = () => {
      if (!association.accounts?.length) return null;

      return (
        <Stack spacing={0}>
          {association.accounts.map(({ id, name }) => (
            <Text key={name}>
              {name} ({id})
            </Text>
          ))}
        </Stack>
      );
    };

    if (!parts?.length) {
      return <Fragment key={index}></Fragment>;
    }
    return (
      <Tooltip key={index} label={tooltipContent()} aria-label="Account Names">
        <Tag size="sm" key={association.id} colorScheme="gray" alignSelf="flex-start">
          {parts.join(' | ')}
        </Tag>
      </Tooltip>
    );
  }, []);

  const handleUpdate = useCallback(
    (updatedContact: ContactHit) => {
      onUpdate?.(updatedContact);
    },
    [onUpdate],
  );

  const onEditClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    openModal('Contact', {
      isOpen: true,
      onUpdate: handleUpdate,
      onClose: () => closeModal('Contact'),
      contactId: Number(contact.id),
    });
  };

  const onCreateClick = () => {
    openModal('Contact', {
      isOpen: true,
      onClose: () => closeModal('Contact'),
      initialState: {
        preferred_name: contact.full_name,
        email: contact.email,
        associations: [{ accounts: [request.account_id] }],
      },
      onContactCreate: async (newContact) => {
        await refreshContactSearch();
        onRemoveContact(contact);
        onSelectContact({ ...newContact, existsInSystem: true });
      },
    });
  };

  return (
    <Fragment key={`${contact.id} ${contact.email}`}>
      <Stack
        direction="column"
        minH={16}
        p={2}
        borderWidth={1}
        borderRadius="md"
        borderColor={borderColor}
        bg={bg}
        cursor="default"
        onClick={handleClick}
        role="group"
      >
        <Stack direction="row" justifyItems="space-between">
          <Stack direction="row" alignItems="center" flex={1}>
            <Box boxSize={6} pos="relative">
              <Avatar pos="absolute" top={0} bg="#508565" size="xs" color="white" name={avatarName} />
              <IconButton
                pos="absolute"
                top={0}
                size="xs"
                variant="solid"
                colorScheme="blackAlpha"
                aria-label="Edit Contact"
                icon={<HiPencil />}
                onClick={onEditClick}
                borderRadius="full"
                opacity={0}
                _hover={{ opacity: 1 }}
              />
            </Box>
            <Heading size="xs" flex="1">
              {name}
            </Heading>
          </Stack>
          <IconButton
            size="xs"
            variant="ghost"
            aria-label="Deselect Contact"
            icon={<HiXMark />}
            onClick={() => onRemove(contact)}
            opacity="0"
            _groupHover={{ opacity: isSelected ? 1 : 0 }}
          />
        </Stack>
        <Stack direction="column" spacing={0}>
          <Text fontSize="xs">{contact?.email}</Text>
          <Show if={typeof contact?.existsInSystem === 'boolean'}>
            <HStack justifyContent="space-between">
              <Image
                src={getAsset('front.png')}
                h="4"
                w="4"
                title={
                  contact?.existsInSystem
                    ? 'Included due to being in emails related to this request'
                    : "This contact is not in the system, but matches the customer's email addresses."
                }
              />
              <Show if={contact?.existsInSystem === false}>
                <IconButton size="xs" icon={<HiPlus />} aria-label="Add from Front" onClick={onCreateClick} />
              </Show>
            </HStack>
          </Show>

          <Show if={!!contact?.associations?.length}>
            <HStack gap={1} mt={2} flexWrap="wrap" w="full">
              {contact?.associations?.map(renderAssociation)}
            </HStack>
          </Show>
        </Stack>
      </Stack>
    </Fragment>
  );
};
